// Constants
import {
  MULTIPLE_FILES_NOT_ALLOWED,
  FOLDER_NOT_ALLOWED,
  INCORRECT_FILE_SIZE,
  INCORRECT_FILE_TYPE,
  DEFAULT,
} from '../constants/errorConstants';

/**
 * FileUpload Service
 * It handles everything about the browser file upload process.
 */
class FileUpload {
  /**
   * Class constructor.
   * @property {RegExp} imageTypeRexeg regular expression for image validation.
   * @property {Number} imageFileSize  file size allowed.
   */
  constructor(imageTypeRexeg, imageFileSize) {
    /**
     * Max image size constant
     * @type {RegExp}
     */
    this.DEFAULT_MAX_IMAGE_SIZE = 5000000;

    /**
     * Regular expression to validate the supported mime types used by the File API (based on the file extension).
     * If not specified use default one.
     * @type {RegExp}
     */
    this.imageExtensionRegex = imageTypeRexeg || /image\/(jpg|jpeg|png)$/i;

    /**
     * Regular expressions to validate the supported mime types, based on the file content.
     * The full list of magic numbers can be found here:
     * https://mimesniff.spec.whatwg.org/#matching-an-image-type-pattern
     * @type {RegExp}
     */
    this.mimeTypesRegex = [
      // PNG files magic number
      /^89504E470D0A1A0A/i,
      // JPG-JPEG files magic number
      /^FFD8FF/i,
    ];

    /**
     * File size allowed. if not specified use default one.
     * @type {Number}
     */
    this.imageFileSize = imageFileSize || this.DEFAULT_MAX_IMAGE_SIZE;

    /**
     * @type {Object}
     */
    this.window = window;
  }

  /**
   * It validates the type of an image.
   * @param {String} type The image type.
   * @return {Boolean}
   */
  _isAnImageType(type) {
    return this.imageExtensionRegex.test(type);
  }

  /**
   * It validates the file content.
   * @param {Object} file The file to check
   * @returns {Promise.<Boolean>} It resolves with a boolean value that tells whether the content is valid or not
   */
  _isFileContentValid(file) {
    // Extract the first 8 bytes of the file.
    const fileHeader = file.slice(0, 8);
    const fileReader = new window.FileReader();

    return new Promise((resolve, reject) => {
      // Event handler to execute when the file has been read.
      fileReader.onloadend = (event) => {
        // Extract the bytes array.
        const bytesArray = new Uint8Array(event.target.result);

        // This variable will store the header as a string for a later checking.
        let header = '';
        bytesArray.forEach((byte) => {
          /* Prepend zeros and trim only the last two characters.
          This way, if a single digit hex value is found, it will have a leading zero.
          eg: byte.toString(16) => A
          then `0A` is constructed, and finally, the last two characters are extracted,
          thus leaving `0A` as the bytes to add to the header. */
          header += `0${byte.toString(16)}`.slice(-2);
        });

        // Check the header and resolve the promise with the result of the mime type checking.
        resolve(this._isMimeTypeValid(header));
      };

      // Reject the promise, only of there was an error while processing the file.
      fileReader.onerror = (error) => {
        reject(error);
      };

      // Process the file header.
      fileReader.readAsArrayBuffer(fileHeader);
    });
  }

  /**
   * Validates the mime type header using magic numbers.
   * @param {String} headerString The header to check.
   * @returns {Boolean}
   */
  _isMimeTypeValid(headerString) {
    // Check if the header signature matches any of the valid mime types.
    return this.mimeTypesRegex.some(regex => regex.test(headerString));
  }

  /**
   * A valid object returned by the `_validateImageFile` promise
   * @typedef {Object} validatedFileObject
   * @property {Boolean} valid Whether the file is valid or not.
   * @property {String} error The error code.
   */

  /**
   * It validates the uploaded image file.
   * This method validates two arrays. `items` is undefined when the user upload an image from IE. Items is
   * used by Firefox and Chrome in order to know if the file is a folder or not.
   * - length (only one file)
   * - it should be a file, not a folder
   * - type
   * - size
   *
   * The Promise returned is only rejected if an error occurred (For example, while processing the file).
   * An invalid file is not considered an error, so the result of the validation is returned in the resolve callback.
   * The resolved object has a `validatedFileObject` structure (see the typedef for validatedFileObject above).
   *
   * @param {Array} files array of `files` that the method will validate.
   * @param {Array} items array of `items` that the method will validate.
   * @returns {Promise.<validatedFileObject>}
   */
  _validateImageFile(files, items) {
    const error = {
      status: false,
      code: '',
    };

    return new Promise((resolve, reject) => {
      if (files.length > 1) {
        error.status = true;
        error.code = MULTIPLE_FILES_NOT_ALLOWED;
      } else if (items && items[0].webkitGetAsEntry && items[0].webkitGetAsEntry().isDirectory) {
        error.status = true;
        error.code = FOLDER_NOT_ALLOWED;
      } else if (!this._isAnImageType(files[0].type)) {
        error.status = true;
        error.code = INCORRECT_FILE_TYPE;
      } else if (files[0].size > this.imageFileSize) {
        error.status = true;
        error.code = INCORRECT_FILE_SIZE;
      }

      // Stop processing the file if an error was already found.
      if (error.status) {
        resolve({ valid: false, error: error.code });
      } else {
        // Previous checks passed. Check the file content now.
        this._isFileContentValid(files[0])
          .then((valid) => {
            if (valid) {
              // No error object is needed if the file is valid. Resolve only with the `valid` property.
              resolve({ valid: true });
            } else {
              // The file is invalid. Prepare the error object and resolve the promise with that value.
              resolve({ valid: false, error: INCORRECT_FILE_TYPE });
            }
          })
          .catch((err) => {
            // The reject callback should only be called if an error occurred.
            reject(err);
          });
      }
    });
  }

  /**
   * It will revoke the object URL which was previously created.
   * @param {String} objectUrl DOMString representing the object URL that was created by calling `createObjectURL`.
   */
  revokeObjectUrl(objectUrl) {
    this.window.URL.revokeObjectURL(objectUrl);
  }

  /**
   * This method allows the validation and the upload of a single file.
   * It creates an object URL with the uploaded file.
   * @param {Object} files FileList object of the file to upload
   * @param {Object} items optional.DataTrasnferItemList data object of the dropped files.
   */
  getFileImage(files, items = null) {
    return new Promise((resolve, reject) => {
      /**
       * If the `files` array is empty it means that the user is trying to upload a folder from IE or
       * dropping an existing element of the page.
       */
      if (!Object.keys(files).length) {
        // Reject the promise with the default error code.
        reject(DEFAULT);
      } else {
        this._validateImageFile(files, items)
          .then(({ valid, error }) => {
            if (valid) {
              resolve(this.window.URL.createObjectURL(files[0]));
            } else {
              reject(error);
            }
          })
          .catch(() => {
            /* If any error is caught, reject the promise with the default error message.
             These errors are generally thrown by 3rd party libs so we don't need to display
             any specific message in the UI for them. */
            reject(DEFAULT);
          });
      }
    });
  }
}

export default FileUpload;



// WEBPACK FOOTER //
// ./src/services/FileUpload.js